import { Box, Button, Container, Toolbar, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CodeBackground } from './CodeBackground';
import { ROUTES } from '@/config/app.config';
import { useThemeStore } from '@/stores/use-theme-store';

export function HeroSection() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mode } = useThemeStore();

    return (
        <Box
            sx={{
                position: 'relative',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                background: mode === 'dark'
                    ? 'linear-gradient(180deg, #11132B 0%, #1A1C3E 50%, #252851 100%)'
                    : 'linear-gradient(180deg, #FAFBFC 0%, #F0F2F5 50%, #E8EAF0 100%)',
            }}
        >
            <CodeBackground />

            {/* Gradient overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: mode === 'dark'
                        ? 'radial-gradient(ellipse at center, transparent 0%, rgba(17,19,43,0.6) 70%)'
                        : 'radial-gradient(ellipse at center, transparent 0%, rgba(250,251,252,0.6) 70%)',
                    pointerEvents: 'none',
                }}
            />

            {/* Accent glow */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '20%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '600px',
                    height: '600px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(243,147,35,0.15) 0%, transparent 70%)',
                    filter: 'blur(60px)',
                    pointerEvents: 'none',
                }}
            />

            <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                <Toolbar />

                {/* Badge */}
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2.5,
                        py: 0.75,
                        mb: 4,
                        borderRadius: 50,
                        bgcolor: mode === 'dark' ? 'rgba(243,147,35,0.15)' : 'rgba(243,147,35,0.1)',
                        border: '1px solid',
                        borderColor: mode === 'dark' ? 'rgba(243,147,35,0.3)' : 'rgba(243,147,35,0.2)',
                    }}
                >
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#F39323',
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                            },
                        }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.8rem' }}>
                        Model Context Protocol
                    </Typography>
                </Box>

                <Typography
                    variant="h1"
                    sx={{
                        fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                        fontWeight: 800,
                        lineHeight: 1.1,
                        mb: 3,
                        background: mode === 'dark'
                            ? 'linear-gradient(135deg, #FFFFFF 0%, #F5A854 50%, #EE7677 100%)'
                            : 'linear-gradient(135deg, #1F2937 0%, #F39323 50%, #E84B4C 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    {t('landing.hero.title')}
                </Typography>

                <Typography
                    variant="h5"
                    sx={{
                        color: 'text.secondary',
                        mb: 5,
                        fontWeight: 400,
                        maxWidth: 600,
                        mx: 'auto',
                        lineHeight: 1.6,
                        fontSize: { xs: '1rem', sm: '1.2rem' },
                    }}
                >
                    {t('landing.hero.subtitle')}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate(ROUTES.register)}
                        sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 700,
                            borderRadius: 3,
                            boxShadow: '0 4px 24px rgba(243,147,35,0.4)',
                        }}
                    >
                        {t('landing.hero.cta')}
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => {
                            document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: 3,
                            borderColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                            color: 'text.primary',
                            '&:hover': {
                                borderColor: 'primary.main',
                            },
                        }}
                    >
                        {t('landing.hero.secondaryCta')}
                    </Button>
                </Box>
            </Container>
        </Box>
    );
}

export default HeroSection;
